import { SelectDropdown } from "../SelectDropdown"
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, BarChart, Bar } from "recharts";
import { monthsLowerCase, monthsUpperCase } from "../../consts/forecast";
import { useState } from "react";

export const GraphPerformance = ({
    forecastProducts,
    graphVariant,
    setGraphVariant,
    graphView,
    setGraphView,
    selectedForecastYear,
    profitAndLossMonthlyMetrics,
    adSpendTotals,
    revenueTotals
}) => {
    const [showRevenueForecast, setShowRevenueForecast] = useState(true);
    const [showRevenueActual, setShowRevenueActual] = useState(true);
    const [showCMForecast, setShowCMForecast] = useState(true);
    const [showCMActual, setShowCMActual] = useState(true);
    const emptyMonths = (monthsLowerCase || []).reduce((acc, m) => ({ ...acc, [m]: 0 }), {});

    const sumByActualsMonthly = { ...emptyMonths };
    const sumByForecastMonthly = { ...emptyMonths };
    (forecastProducts || []).forEach((product) => {
        const actuals = product?.monthly_actuals_by_month || {};
        const forecast = product?.monthly_forecast_by_month || {};
        (monthsLowerCase || []).forEach((m) => {
            sumByActualsMonthly[m] += Number(actuals[m] || 0);
            sumByForecastMonthly[m] += Number(forecast[m] || 0);
        });
    });

    const pct = (num, den) => (Number(den) <= 0 ? 0 : (Number(num) / Number(den)) * 100);
    const monthlyVariancePct = (monthsLowerCase || []).map((m) => pct(sumByActualsMonthly[m], sumByForecastMonthly[m]));

    // Build chart rows depending on variant. For revenue and CM we place values into generic keys `forecast` and `actual` for reuse.
    const chartData = (monthsLowerCase || []).map((m, i) => {
        const base = {
            month: monthsUpperCase?.[i] || m,
            variancePct: Number(monthlyVariancePct[i] || 0),
        };
        if (graphVariant === "cm") {
            const cmForecast = (Number(revenueTotals?.[m] || 0) / 1.3) - Number(adSpendTotals?.[m] || 0);
            const cmActual = Number(profitAndLossMonthlyMetrics?.[m]?.contributionMargin || 0);
            return {
                ...base,
                forecast: Number(cmForecast || 0),
                actual: Number(cmActual || 0),
            };
        }
        // default revenue
        return {
            ...base,
            forecast: Number(sumByForecastMonthly[m] || 0),
            actual: Number(sumByActualsMonthly[m] || 0),
        };
    });

    const isLine = graphView === "line";
    const isRevenue = graphVariant === "revenue";
    const isCM = graphVariant === "cm";

    const formatCurrencyThousands = (value) => {
        const v = Number(value || 0);
        const sign = v < 0 ? "-" : "";
        const abs = Math.abs(v);
        if (abs >= 1000) {
            const thousands = abs / 1000;
            const formatted = (thousands >= 100)
                ? Math.round(thousands).toLocaleString("en-US")
                : (Math.round(thousands * 10) / 10).toLocaleString("en-US");
            return `$${sign}${formatted}K`;
        }
        return `$${Math.round(v).toLocaleString("en-US")}`;
    };
    return (
        <div className="space-y-6">
            <div className="bg-[#f5f5f5] flex flex-col gap-6 rounded-xl border py-6 shadow-sm px-6">
                <div className="flex items-center justify-between gap-3">
                    <div className="leading-none font-semibold">
                        Forecast vs Actual Performance
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relatibe w-48">
                            <SelectDropdown 
                                options={[
                                    { label: "Revenue", value: "revenue" },
                                    { label: "Variance", value: "variance" },
                                    { label: "Contribution Margin", value: "cm" },
                                ]}
                                value={graphVariant}
                                onChange={(v) => {
                                    setGraphVariant(v);
                                }}
                                selectClassname="!bg-transparent"
                            />
                        </div>
                        <div className="relatibe w-48">
                            <SelectDropdown 
                                options={[
                                    { label: "Line", value: "line" },
                                    { label: "Bar", value: "bar" },
                                ]}
                                value={graphView}
                                onChange={setGraphView}
                                selectClassname="!bg-transparent"
                            />
                        </div>
                    </div>
                </div>
                <div className="bg-green-50 inline-flex h-9 items-center justify-center rounded-lg py-1 px-2 w-full">
                    <button
                        onClick={() => setGraphVariant("revenue")}
                        className={`${graphVariant === "revenue" ? "bg-white" : ""} inline-flex h-[calc(100%-3px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 flex-1`}
                    >
                        Revenue
                    </button>
                    <button
                        onClick={() => setGraphVariant("variance")}
                        className={`${graphVariant === "variance" ? "bg-white" : ""} inline-flex h-[calc(100%-3px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 flex-1`}
                    >
                        Variance
                    </button>
                    <button
                        onClick={() => setGraphVariant("cm")}
                        className={`${graphVariant === "cm" ? "bg-white" : ""} inline-flex h-[calc(100%-3px)] items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 flex-1`}
                    >
                        Contribution Margin
                    </button>
                </div>
                {(isRevenue || isCM) && (
                    <div className="flex items-center gap-2 justify-end">
                        <button 
                            onClick={() => (isRevenue ? setShowRevenueForecast((v) => !v) : setShowCMForecast((v) => !v))} 
                            className={`inline-flex items-center gap-2 px-2 py-1 rounded ${(isRevenue ? showRevenueForecast : showCMForecast) ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}
                        >
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
                            {isRevenue ? "Forecast" : "CM Forecast"}
                        </button>
                        <button 
                            onClick={() => (isRevenue ? setShowRevenueActual((v) => !v) : setShowCMActual((v) => !v))} 
                            className={`inline-flex items-center gap-2 px-2 py-1 rounded ${(isRevenue ? showRevenueActual : showCMActual) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}
                        >
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                            {isRevenue ? "Actual" : "CM Actual"}
                        </button>
                    </div>
                )}
                <section className="relative h-[660px]">
                    <ResponsiveContainer width="100%" height="100%">
                        {isLine ? (
                            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyThousands(v)} tickMargin={8} hide={!(isRevenue || isCM)} />
                                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(v)}%`} hide={(isRevenue || isCM)} />
                                <Tooltip formatter={(value, name) => [(isRevenue || isCM) ? formatCurrencyThousands(value) : `${Number(value).toFixed(1)}%`, name]} />
                                {(isRevenue || isCM) ? (
                                    <>
                                        {(isRevenue ? showRevenueForecast : showCMForecast) && (
                                            <Line yAxisId="left" type="monotone" dataKey="forecast" stroke="#3b82f6" name={isRevenue ? "Forecast" : "CM Forecast"} dot={false} />
                                        )}
                                        {(isRevenue ? showRevenueActual : showCMActual) && (
                                            <Line yAxisId="left" type="monotone" dataKey="actual" stroke="#10b981" name={isRevenue ? "Actual" : "CM Actual"} dot={false} />
                                        )}
                                    </>
                                ) : (
                                    <Line yAxisId="right" type="monotone" dataKey="variancePct" stroke="#8b5cf6" name="% vs plan" dot={false} />
                                )}
                            </LineChart>
                        ) : (
                            <BarChart data={chartData} margin={{ top: 10, right: 30, left: 30, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="month" />
                                <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyThousands(v)} tickMargin={8} hide={!(isRevenue || isCM)} />
                                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(v)}%`} hide={(isRevenue || isCM)} />
                                <Tooltip formatter={(value, name) => [(isRevenue || isCM) ? formatCurrencyThousands(value) : `${Number(value).toFixed(1)}%`, name]} />
                                {(isRevenue || isCM) ? (
                                    <>
                                        {(isRevenue ? showRevenueForecast : showCMForecast) && (
                                            <Bar yAxisId="left" dataKey="forecast" fill="#3b82f6" name={isRevenue ? "Forecast" : "CM Forecast"} />
                                        )}
                                        {(isRevenue ? showRevenueActual : showCMActual) && (
                                            <Bar yAxisId="left" dataKey="actual" fill="#10b981" name={isRevenue ? "Actual" : "CM Actual"} />
                                        )}
                                    </>
                                ) : (
                                    <Bar yAxisId="right" dataKey="variancePct" fill="#8b5cf6" name="% vs plan" />
                                )}
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </section>
            </div>
        </div>
    )
}